import {
  ArchiveIcon,
  ArrowCounterClockwiseIcon,
  ArrowLeftIcon,
  ArrowSquareOutIcon,
  ArrowUUpLeftIcon,
  ArrowUUpRightIcon,
  CheckCircleIcon,
  DotsThreeIcon,
  DownloadSimpleIcon,
  FloppyDiskIcon,
  ImageSquareIcon,
  NewspaperClippingIcon,
  SidebarSimpleIcon,
  StackIcon,
} from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { StoredContextDisclosure } from '@/components/briefs/StoredContextDisclosure';
import { ConceptFieldsSection } from '@/components/concept-workbench/ConceptFieldsSection';
import {
  AD_PLATFORM_OPTIONS,
  ConceptPreviewPane,
} from '@/components/concept-workbench/ConceptPreviewPane';
import {
  RegenerateAllSizesChip,
  RegenerateSizeChip,
} from '@/components/concept-workbench/RegenerateSizeChip';
import { WorkbenchBriefFields } from '@/components/concept-workbench/WorkbenchBriefFields';
import { WorkbenchInspector } from '@/components/concept-workbench/WorkbenchInspector';
import { WorkbenchStage } from '@/components/concept-workbench/WorkbenchStage';
import { attachExistingMedia } from '@/components/concept-workbench/existing-media-source';
import {
  PROMPT_MODES,
  isModeAvailable,
  resolvePromptTarget,
} from '@/components/concept-workbench/prompt-target';
import { useConceptWorkbench } from '@/components/concept-workbench/useConceptWorkbench';
import { PageHeaderActionLayout } from '@/components/layout/PageHeaderActionLayout';
import { AutoSaveIndicator } from '@/components/studio/editor';
import { Button } from '@/components/ui/button';
import { CircleIconButton } from '@/components/ui/circle-icon-button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  useDialogContentContainer,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { QualityChip } from '@/components/ui/inspector/QualityChip';
import { ConceptDesignBreadcrumb } from '@/components/ui/inspector/bodies/ConceptDesignBreadcrumb';
import type { InlinePromptComposerHandle } from '@/components/ui/inspector/shared/InlinePromptComposer';
import { SegmentedToggle } from '@/components/ui/segmented-toggle';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { StatusBadge, statusBadgeVariants } from '@/components/ui/status-badge';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  ExistingMediaSheet,
  FloatingPromptBar,
  LayersPanel,
  type RatioVariant,
  type VariantState as StripVariantState,
  type WorkbenchLayer,
  WorkbenchPromptModeRail,
} from '@/components/workbench';
import type { AdPlatformId } from '@/constants/ad-specs';
import { useIsMobile } from '@/hooks/use-mobile';
import { useBrandKits } from '@/hooks/useBrandKits';
import { useBriefConcept } from '@/hooks/useBriefConcept';
import { useBriefWorkspace } from '@/hooks/useBriefWorkspace';
import { useMediaLibrary } from '@/hooks/useMediaLibrary';
import { useCanSplitRightSlot } from '@/hooks/useRightSlotBreakpoints';
import { useWorkbenchPrompt } from '@/hooks/useWorkbenchPrompt';
import { resolveBrandFontFamily } from '@/lib/concept-workbench/brand-font';
import { sizesFromOlderRenderer } from '@/lib/concept-workbench/card-renderer';
import {
  type DesignPromptSource,
  isFlatDesignDirection,
  planGenerateThisSize,
  resolveDesignPromptGate,
  resolveSiblingDesignReference,
  siblingDesignInstruction,
} from '@/lib/concept-workbench/derive-design-prompt';
import { nextLayerOrder } from '@/lib/concept-workbench/layer-order';
import {
  READY_GATE_CHIP_COPY,
  readyGateChipVerdict,
} from '@/lib/concept-workbench/ready-gate-quality';
import { CONCEPT_DISPLAY_LABEL } from '@/lib/concept-workbench/status-vocabulary';
import type { AspectRatioToken } from '@/lib/concept-workbench/types';
import { useVariantSetStore } from '@/lib/concept-workbench/useVariantSetStore';
import { requestCompositedUrl } from '@/lib/media-studio/request-composite';
import { toMatrixPlatform } from '@/lib/platform-matrix/placement-matrix';
import { cn } from '@/lib/utils';
import type { BriefConceptWrite } from '@/services/data/BriefConceptEditorService';
import type { MediaFile } from '@/types/studio';
import { downloadBlob } from '@/utils/downloadBlob';
import { logger } from '@/utils/logger';

/**
 * THE CONCEPT WORKBENCH — `/briefs/:briefId/concepts/:projectTaskId/design`.
 * (Was `/creatives/briefs/...` until 2026-08-15; that prefix now only exists as a
 * redirect, because the sidebar stopped nesting anything under Creatives.)
 *
 * A full-surface ROUTE, not a dialog. `FullScreenSplitDialog` is licensed for
 * ">30s authoring with live preview" but it is TWO panes; it cannot host a third
 * column, a canvas that owns pan/zoom, and a floating overlay without fighting
 * the Radix focus trap. The nearest in-repo precedent, `Inspector`'s `workspace`
 * presentation, is already a full-bleed `absolute inset-0` surface with a
 * hand-rolled portal — i.e. the repo has ALREADY concluded that a real workbench
 * escapes dialog chrome. A route is that conclusion, honestly named.
 *
 * It is a SIBLING of `BriefConceptPage`, not a replacement (ruling O7). The two
 * share the concept row, the RPC and the optimistic-locking contract; the form
 * page stays the canonical place to write a brief from scratch.
 *
 * BREAKPOINTS follow `useRightSlotBreakpoints`, the app's existing answer, rather
 * than inventing thresholds:
 *   ≥1024  three columns
 *   768–1023  canvas + inspector, left column in a Sheet
 *   <768   canvas only, both columns in Sheets, and the stage mounts READ-ONLY.
 *
 * The mobile degradation is not a shortcut. Konva direct manipulation at 390px
 * means a 44px thumb over a 7px transform anchor; shipping something that LOOKS
 * editable and is not would be a false pass. Pan and zoom stay, the layers sheet
 * still selects, and the inspector's numeric rows stay fully editable — which is
 * the honest correction path on a phone.
 *
 * The app sidebar is NOT auto-collapsed here (ruling O1). That decision was
 * reversed for list pages on 2026-08-11 and re-opening it on a new route would
 * re-litigate shared navigation for a local convenience.
 */

/**
 * The canvas is the elastic middle and the side columns tier up, exactly as the
 * post inspector's right rail does — a flat width crushes the preview at narrow
 * desktop widths.
 *
 * THE BASE TIER'S CENTRE TRACK IS `minmax(0,1fr)`, NOT `minmax(360px,1fr)`, and
 * that is not a detail. At exactly 1024px the app sidebar takes ~216px, so a
 * 360px floor on the canvas pushed the grid's total past the viewport and the
 * right inspector was clipped off-screen — the column was rendered, scored and
 * unusable. A `0` floor lets the canvas absorb the shortfall instead. The real
 * minimums come back at `xl`, where there is genuinely room for them.
 *
 * WHY (measured, Hightouch Ad Studio, 2026-08-12): layers 210px · canvas ~700px ·
 * inspector 244px, i.e. the canvas is ~61% of the shell.
 *
 * THE TWO RAILS ARE DELIBERATELY ASYMMETRIC, and the asymmetry follows what each
 * one holds:
 *  - LEFT tracks the reference almost exactly (216px). It holds one list of short
 *    layer names and nothing else, so extra width buys nothing and costs canvas.
 *    An earlier pass ran it at 260–320px and the founder's read was immediate:
 *    "the left panel is too wide."
 *  - RIGHT goes WIDER than the reference on purpose (320 → 400px). Their 244px
 *    inspector holds a ratio strip and single-value property rows; ours also
 *    carries the Content copy rows, whose metadata line reads
 *    "114 characters · 18 words · Warm, calm" and, per `CopyVariantCard`'s own
 *    comment, wrapped MID-PHRASE at 300px once a truncation warning made it four
 *    parts. Matching their 244px would reproduce a defect we already fixed once.
 */
const GRID_COLUMNS_SHELL =
  'grid min-h-0 flex-1 transition-[grid-template-columns] duration-200 ease-out';

/**
 * TWO TEMPLATES, ONE SHELL. Preview mode has nothing actionable in the Layers
 * rail — selection, drag, ⌥↑/⌥↓ all edit the design, and Preview is "see the
 * ad in its placement," not a place to restack layers — so the rail's track
 * collapses to `0px` at every breakpoint rather than staying 216–232px of
 * inert chrome (`B-workbench-preview.png`, docs/planning/HANDOFF-EDITOR-
 * UNIFICATION-2026-08-12.md §8.11/§8.14 P1). `transition-[grid-template-
 * columns]` is the same animated-track pattern `sidebar.tsx` already uses for
 * its own collapse, so the canvas widens instead of the column vanishing.
 */
const GRID_TEMPLATE_DESIGN = cn(
  'grid-cols-[216px_minmax(0,1fr)_320px]',
  'xl:grid-cols-[224px_minmax(420px,1fr)_360px]',
  '2xl:grid-cols-[232px_minmax(480px,1fr)_400px]',
);
const GRID_TEMPLATE_PREVIEW = cn(
  'grid-cols-[0px_minmax(0,1fr)_320px]',
  'xl:grid-cols-[0px_minmax(420px,1fr)_360px]',
  '2xl:grid-cols-[0px_minmax(480px,1fr)_400px]',
);

/**
 * PANEL CONTRAST — THERE IS EXACTLY ONE TONAL CHANGE ON THIS SCREEN, and it is
 * the well behind the artwork.
 *
 * WHY (measured, Hightouch): their layers panel, their inspector AND their header
 * are all `#FFFFFF` — the same value — and only the page background behind the
 * canvas is a hair darker. One step, on the well, with `0.5px` hairlines doing
 * every other piece of structural work. That single restraint is most of why it
 * reads clean.
 *
 * An earlier pass here did the opposite: `bg-canvas` on the stage and `bg-card`
 * on both rails. With the app sidebar already tinted that produced three
 * competing tonal bands with the artwork in the only untinted region — founder:
 * "it's like there are sidebars everywhere." So the rails and the mobile control
 * bar carry NO fill of their own; they sit on the page and are separated from the
 * canvas by the hairline alone.
 *
 * HAIRLINES. Theirs is `0.5px solid oklch(0.9401 0 0)`, one edge only, never a
 * card or an outline. Mora expresses the same intent as `border-border/40` — this
 * repo's hairline — because a fixed near-white value goes wrong the moment the
 * page is dark, and our border token tracks the theme.
 */
const RAIL_SURFACE = 'min-h-0 min-w-0 overflow-hidden';

/**
 * Initial page for the existing-media sheet's `useMediaLibrary()` read.
 * Mirrors `CATALOG_PAGE_SIZE` in `server/api/paid-ad/source-media-catalog.post.ts`
 * (the nearest shipped picker of this exact shape) — not imported from it, since
 * that module is server-only, but kept at the same value deliberately.
 */
const EXISTING_MEDIA_PAGE_SIZE = 48;

/**
 * The `+` menu, modelled on the ChatGPT-style list the founder specified for the
 * composer's `+`: a name and one line of what it is, per row.
 *
 * `Text` and `Shape` are the two layer kinds the store can always seed
 * honestly. `Brand lockup` (W-2) is the third, added ONLY once a real
 * brand-kit logo has resolved — see `brandLogoUrl` below — because offering it
 * before it works is the silent-no-op this repo keeps shipping. When no logo
 * has resolved the row still renders, disabled, with the honest reason instead
 * of vanishing outright: a marketer who owns a brand kit with no logo yet
 * should be told what to fix, not left wondering why the row is missing. A
 * `Headline from the brief` row is still not offered — it needs the per-ratio
 * band builder, which does not exist yet.
 */
const BASE_ADD_LAYER_ITEMS = [
  { id: 'text', label: 'Text', description: 'A headline, subline, or caption you can style' },
  { id: 'shape', label: 'Shape', description: 'A block or scrim to sit behind your type' },
] as const;

const DESIGN_PROMPT_SOURCE_COPY: Record<DesignPromptSource, string> = {
  subject: 'Generate one image from this concept’s visual direction.',
  proof: 'Generate one image that states this concept’s sourced proof.',
  headline: 'Generate one image from this concept’s headline.',
  rationale: 'Generate one image from this concept’s rationale.',
};

function stripStateFor(variant: {
  isGenerating: boolean;
  mediaId: string | null;
  needsReview: boolean;
}): StripVariantState {
  if (variant.isGenerating) return 'generating';
  if (!variant.mediaId) return 'empty';
  if (variant.needsReview) return 'needs-review';
  return 'ready';
}

type WorkbenchDraft = BriefConceptWrite & { referenceUrls: string[] };

function draftFromConcept(concept: {
  audienceId: string | null;
  angleId: string | null;
  rationale: string | null;
  hook: string | null;
  summary: string | null;
  proof: BriefConceptWrite['proof'];
  cta: string | null;
  visualDirection: BriefConceptWrite['visualDirection'];
  format: BriefConceptWrite['format'];
  intents: readonly { intent: 'organic' | 'paid'; platform: string | null }[];
  referenceUrls: readonly { url: string }[];
}): WorkbenchDraft {
  return {
    audienceId: concept.audienceId,
    angleId: concept.angleId,
    rationale: concept.rationale,
    hook: concept.hook,
    summary: concept.summary,
    proof: concept.proof,
    cta: concept.cta,
    visualDirection: concept.visualDirection,
    format: concept.format,
    intents: concept.intents.map(({ intent, platform }) => ({ intent, platform })),
    referenceUrls: concept.referenceUrls.map((reference) => reference.url),
  };
}

export interface ConceptDesignBodyProps {
  briefId: string;
  projectTaskId: string;
  onClose: () => void;
}

export function ConceptDesignBody({
  briefId,
  projectTaskId,
  onClose,
}: ConceptDesignBodyProps): JSX.Element {
  const isMobile = useIsMobile();
  const canSplit = useCanSplitRightSlot();

  const workbench = useConceptWorkbench(briefId, projectTaskId);
  const { concept, conceptQuery, persistence, document, selectedVariant, selectedLayer } =
    workbench;

  const conceptEditor = useBriefConcept(briefId, projectTaskId);

  /**
   * FIX 1 — the breadcrumb data source. `useConceptWorkbench`/`useBriefConcept`
   * expose the Concept row (which carries `hook`) but never the Brief's own
   * `name` — that field only travels through `useBriefWorkspace`, the same
   * hook `BriefConceptPage` already calls for exactly this purpose (see its
   * `briefName` derivation). This is a genuinely NEW query at this call site
   * (the workbench never fetched the workspace before), not a payload already
   * in hand — flagged per the task brief rather than silently added. In the
   * common path (open a Concept from the Brief detail page, or from
   * `BriefConceptPage` itself) this hits a warm TanStack cache under the same
   * query key, so it costs nothing extra; a cold deep link pays one query.
   * While it loads, `briefName` falls back to the literal 'Brief' — never a
   * guessed or invented name.
   */
  const workspaceQuery = useBriefWorkspace(briefId);
  const briefName = workspaceQuery.data?.brief.name ?? 'Brief';

  /**
   * W-2's resolution seam. `activeKit` is the SAME hook `ConceptPreviewPane`
   * already reads its logo from, so "does a logo exist" can never disagree
   * between the `+` menu's honesty gate and what Preview actually renders.
   * `logo_url` (primary) gates whether `Brand lockup` is offered at all;
   * `logo_light_url` / `logo_dark_url` are offered to the Variant dropdown
   * once a layer exists, and default to primary when a kit only has the one
   * mark — most do.
   */
  const { activeKit } = useBrandKits();
  const brandLogoUrl = activeKit?.logo_url?.trim() || null;
  const brandLogoUrls = useMemo(
    () => ({
      primary: brandLogoUrl,
      light: activeKit?.logo_light_url?.trim() || null,
      dark: activeKit?.logo_dark_url?.trim() || null,
    }),
    [brandLogoUrl, activeKit?.logo_light_url, activeKit?.logo_dark_url],
  );

  // A NEW TEXT LAYER'S FONT COMES FROM THE BRAND KIT, NOT A HARDCODED 'Inter'.
  // See `resolveBrandFontFamily` for the fallback chain and why it refuses to
  // guess at fonts the editor can't reproduce end to end.
  const brandFontFamily = useMemo<string>(
    () => resolveBrandFontFamily(activeKit?.font_primary, activeKit?.font_secondary),
    [activeKit?.font_primary, activeKit?.font_secondary],
  );

  const selectVariant = useVariantSetStore((state) => state.selectVariant);
  const selectLayers = useVariantSetStore((state) => state.selectLayers);
  const setLayerVisibility = useVariantSetStore((state) => state.setLayerVisibility);
  const updateSetLayer = useVariantSetStore((state) => state.updateSetLayer);
  const updateGeometry = useVariantSetStore((state) => state.updateGeometry);
  const addLayer = useVariantSetStore((state) => state.addLayer);
  const setLayerOrder = useVariantSetStore((state) => state.setLayerOrder);
  const deleteLayer = useVariantSetStore((state) => state.deleteLayer);
  const undo = useVariantSetStore((state) => state.undo);
  const redo = useVariantSetStore((state) => state.redo);
  const materialise = useVariantSetStore((state) => state.materialise);

  const navigate = useNavigate();

  const [leftSheetOpen, setLeftSheetOpen] = useState(false);
  const [rightSheetOpen, setRightSheetOpen] = useState(false);
  const [briefSheetOpen, setBriefSheetOpen] = useState(false);
  /**
   * The ad-preview platform, lifted out of `ConceptPreviewPane` so the switcher
   * can be pinned to the well's right edge under the Design/Preview toggle
   * rather than floating centred over the canvas. `resolvedAdPlatform` is what
   * the pane reports it is actually showing when the user has chosen nothing —
   * the pane keeps that derivation because it depends on the loaded copy
   * variant, which only the pane reads.
   */
  const [selectedAdPlatform, setSelectedAdPlatform] = useState<AdPlatformId | null>(null);
  const [resolvedAdPlatform, setResolvedAdPlatform] = useState<AdPlatformId | null>(null);
  const [zoom, setZoom] = useState(1);
  /**
   * DESIGN ⇄ PREVIEW. Design is the Konva editing stage, unchanged. Preview
   * composes the same artwork inside the platform chrome it will actually
   * ship in — the ad studio's own answer to "what a marketer actually needs
   * to see before spending a credit or a send." `ConceptPreviewPane` owns the
   * platform/placement resolution and follows the SAME selected size as
   * Design, so switching ratio in the strip changes the placement shown in
   * both modes (4:5 → feed, 9:16 → story/reel) rather than only one of them.
   */
  const [canvasView, setCanvasView] = useState<'design' | 'preview'>('design');
  /**
   * W-1's lift. This body is the parent of BOTH `ConceptCopySection` (via
   * `WorkbenchInspector`, the Content tab) and `ConceptPreviewPane`, so it is
   * the seam where "which option is selected" can be owned once and read by
   * both — see the doc comments on `ConceptCopySection`'s `selectedId` prop
   * and `ConceptPreviewPane`'s `selectedCopyVariantId` prop for the contract.
   * `null` means "nothing clicked yet," and both siblings independently fall
   * back to the same "Option 1" default in that case.
   */
  const [selectedCopyVariantId, setSelectedCopyVariantId] = useState<string | null>(null);
  /**
   * `referenceUrls` is not part of `BriefConceptWrite` — the RPC takes it as a
   * separate argument — but it is part of what the user edits and therefore
   * part of what "unsaved" means, so it rides in the same draft object rather
   * than in a second piece of state the dirty check would forget about.
   */
  const [draft, setDraft] = useState<WorkbenchDraft | null>(null);

  /**
   * The layer half of this surface saves itself 800ms after you stop moving, and
   * until now said so nowhere — `hasUnsavedChanges` has been computed and thrown
   * away since the autosave queue landed. `lastLayerSave` latches the moment the
   * queue drains, which is the only point at which a write is known to have
   * completed; claiming a time before that would be a header inventing a save.
   */
  const layerSaving = persistence.hasUnsavedChanges;
  const [lastLayerSave, setLastLayerSave] = useState<Date | null>(null);
  const wasLayerSaving = useRef(false);
  useEffect(() => {
    if (wasLayerSaving.current && !layerSaving) setLastLayerSave(new Date());
    wasLayerSaving.current = layerSaving;
  }, [layerSaving]);

  useEffect(() => {
    if (!concept) return;
    setDraft((current) => current ?? draftFromConcept(concept));
  }, [concept]);

  const isDirty = useMemo(() => {
    if (!concept || !draft) return false;
    return JSON.stringify(draft) !== JSON.stringify(draftFromConcept(concept));
  }, [concept, draft]);

  const isSaving = conceptEditor.update.isPending;

  const save = useCallback(async () => {
    if (!concept || !draft) return;
    try {
      await conceptEditor.update.mutateAsync({
        ...draft,
        // Blank rows are how a user deletes one; sending them would persist
        // empty `project_files` rows the reader would then render as URLs.
        referenceUrls: draft.referenceUrls.map((url) => url.trim()).filter(Boolean),
        expectedVersion: concept.version,
      });
      toast.success('Concept saved');
    } catch (cause) {
      logger.error('Workbench concept save failed', cause);
      toast.error(cause instanceof Error ? cause.message : 'Save failed.');
    }
  }, [concept, draft, conceptEditor.update]);
  const saveRef = useRef(save);
  saveRef.current = save;

  /**
   * Download flattens the selected size's authored layers — headline, styling,
   * everything the Layers rail shows — into the exported file, through the same
   * server compositor (`/api/media/composite-overlays`) the publish dispatcher
   * and `ImageBody`'s download already use. One renderer, so the file can never
   * silently disagree with what ships.
   *
   * COORDINATE SPACE: `materialise(variantId)` (`materialiseVariant` under the
   * hood) is the store's own seam for "what does this variant actually render"
   * — it is the SAME call `WorkbenchStage` makes to draw Preview mode's
   * composite stage, so what this downloads is what Preview already shows. It
   * resolves each layer's per-ratio geometry (`layer_overrides` on top of the
   * set) at the NATIVE authoring size for the selected ratio (1080×1350 for
   * 4:5, etc — `stage-dimensions.ts`) and stamps that size as `stageWidth`,
   * which is the scale denominator `overlay-svg.ts` divides the real fetched
   * image's pixel width by. Sending anything else — a CSS/display size, a
   * different ratio's stage — would land the text at the wrong position and
   * size for what is actually a different image.
   *
   * No layers ⇒ the request degrades to a plain download, same as before.
   * A compositing failure is surfaced as a real error, never swallowed into a
   * silent fallback to the bare photo — that would just relocate the exact lie
   * this button used to tell honestly, by refusing to run at all.
   */
  const downloadUrl = workbench.selectedImageUrl;
  const download = useCallback(async () => {
    if (!downloadUrl || !selectedVariant) return;
    const toastId = `workbench-download-${selectedVariant.id}`;
    // `4:5` → `4x5` — a bare colon is an illegal filename character on Windows.
    const ratioSlug = selectedVariant.aspectRatio.replace(':', 'x');
    const baseName = `mora-ad-${projectTaskId}-${ratioSlug}`;
    try {
      const layers = materialise(selectedVariant.id);
      if (layers.length === 0) {
        await downloadBlob(downloadUrl, baseName);
        return;
      }
      toast.loading('Flattening layers…', { id: toastId });
      const { url, composited } = await requestCompositedUrl(downloadUrl, layers);
      await downloadBlob(url, `${baseName}.${composited ? 'jpg' : 'png'}`);
      toast.success(composited ? 'Downloaded with your text and styling.' : 'Downloaded.', {
        id: toastId,
      });
    } catch (cause) {
      toast.dismiss(toastId);
      logger.error('Workbench image download failed', cause);
      toast.error(
        cause instanceof Error
          ? cause.message
          : 'Could not apply your text overlay to this download.',
      );
    }
  }, [downloadUrl, selectedVariant, materialise, projectTaskId]);
  const downloadRef = useRef(download);
  downloadRef.current = download;
  const hasDownload = Boolean(downloadUrl);

  const lifecycle = {
    isPending: conceptEditor.archive.isPending || conceptEditor.restore.isPending,
    run: async () => {
      if (!concept) return;
      const restoring = concept.status === 'archived';
      try {
        const mutation = restoring ? conceptEditor.restore : conceptEditor.archive;
        await mutation.mutateAsync(concept.version);
        toast.success(restoring ? 'Concept restored' : 'Concept archived');
      } catch (cause) {
        logger.error('Workbench concept lifecycle change failed', cause);
        toast.error(cause instanceof Error ? cause.message : 'That did not go through.');
      }
    },
  };

  const variants: RatioVariant[] = useMemo(
    () =>
      (document?.variants ?? [])
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((variant) => ({
          ratio: variant.aspectRatio,
          state: stripStateFor(variant),
          thumbnailUrl: variant.mediaId ? workbench.mediaUrls[variant.mediaId] : undefined,
        })),
    [document, workbench.mediaUrls],
  );

  /**
   * Top of the list is top of the z-stack, matching every design tool. The store
   * keys stacking on `zIndex` rather than array order, so the panel sorts
   * descending rather than assuming the array is already in paint order.
   *
   * The BACKGROUND is a synthetic, always-last, always-locked row. There is no
   * `image` layer kind in the engine's store — the generated photograph is the
   * Konva `<Image>` under the stack, not an element. Hiding it from the panel
   * would leave the user's most obvious layer unlabelled, which is exactly how a
   * reviewed competitor ended up with an unnameable layer list.
   */
  const layers: WorkbenchLayer[] = useMemo(() => {
    const rows: WorkbenchLayer[] = (document?.layers ?? [])
      .slice()
      .sort((a, b) => b.zIndex - a.zIndex)
      .map((layer) => ({
        id: layer.id,
        name: layer.name,
        kind: layer.kind,
        hidden: !layer.visible,
      }));
    /**
     * Keyed off the SELECTED VARIANT's image as well as the set's. A variant
     * that owns its own image after an outpaint has `followsSetImage: false`
     * and the set `mediaId` can be null while the canvas is plainly showing a
     * photograph — which is exactly the state this concept is in on prod, and
     * the row was missing. The user's most obvious layer was unlabelled, the
     * precise failure the row exists to prevent.
     */
    if (selectedVariant?.mediaId || document?.mediaId) {
      rows.push({ id: '__background__', name: 'Background', kind: 'image', locked: true });
    }
    return rows;
  }, [document, selectedVariant]);

  /**
   * The concept's delivery platforms, narrowed to the ones the placement matrix
   * actually knows. `toMatrixPlatform` is the existing guard rather than a cast:
   * an unrecognised platform must drop out of the `+` menu, never contribute
   * some other platform's ratio list.
   */
  const matrixPlatforms = useMemo(
    () =>
      (concept?.intents ?? [])
        .map((intent) => toMatrixPlatform(intent.platform))
        .filter((platform): platform is NonNullable<typeof platform> => Boolean(platform))
        .map((platform) => ({ platform })),
    [concept],
  );

  const target = useMemo(() => resolvePromptTarget(selectedLayer), [selectedLayer]);
  const prompt = useWorkbenchPrompt({
    target,
    variantId: selectedVariant?.id ?? null,
    aspectRatio: selectedVariant?.aspectRatio ?? null,
    currentImageUrl: workbench.selectedImageUrl,
    briefContext: concept
      ? {
          conceptId: projectTaskId,
          hook: concept.hook,
          summary: concept.summary,
          rationale: concept.rationale,
          cta: concept.cta,
          visualDirection: concept.visualDirection,
          intents: concept.intents,
          angleId: concept.angleId,
          audienceId: concept.audienceId,
        }
      : null,
    renditionLifecycle: workbench.renditionLifecycle,
  });

  /**
   * THE EXISTING-MEDIA SOURCE (audit unit #9). `useMediaLibrary` is the same
   * workspace-scoped hook `AttachPhotoRail` (Home composer) reads from — its
   * query key includes `pageSize`, so this mounts its own cache entry at
   * `EXISTING_MEDIA_PAGE_SIZE` rather than sharing AttachPhotoRail's 24 or
   * AssetsPage's grid (which doesn't route through this hook's list query at
   * all). `error`/`hasMore`/`loadMore` are surfaced to `ExistingMediaSheet`
   * so a failed read renders as an error, not a false "nothing yet", and a
   * search that only covers the loaded page never renders a false "nothing
   * matching" — see that component's docblock.
   */
  const [existingMediaOpen, setExistingMediaOpen] = useState(false);
  const {
    media: libraryMedia,
    isLoading: isLoadingLibraryMedia,
    error: libraryMediaError,
    hasMore: libraryMediaHasMore,
    loadMore: loadMoreLibraryMedia,
    refetch: refetchLibraryMedia,
  } = useMediaLibrary({ limit: EXISTING_MEDIA_PAGE_SIZE });

  const handleChooseExistingMedia = useCallback(
    async (item: MediaFile) => {
      if (!selectedVariant) return;
      setExistingMediaOpen(false);
      try {
        await attachExistingMedia(selectedVariant.id, item.id, workbench.renditionLifecycle);
        toast.success(`${selectedVariant.aspectRatio} attached`);
      } catch (cause) {
        logger.error('Workbench existing-media attachment failed', cause);
        toast.error(cause instanceof Error ? cause.message : 'That did not attach.');
      }
    },
    [selectedVariant, workbench.renditionLifecycle],
  );

  /**
   * THE HEADER IS OWNED BY THIS BODY, not projected into the app header.
   *
   * This surface is a WORKSPACE `Inspector`, which renders `absolute inset-0`
   * inside `SidebarInset` and therefore covers `PageHeaderManager` entirely.
   * `useHeader` would still accept the title and actions; nothing would ever
   * draw them. So the body renders its own 48px chrome row, back-arrow
   * included, and closing is the inspector's own close rather than a route
   * `<Link>` — which is also why there is no second header stacked under a
   * first one.
   */
  const container = useDialogContentContainer();

  // The concept's human identity — its hook — not the type label
  // `getConceptDisplayLabel` returns ('Ad concept' / 'Post concept' /
  // 'Creative concept'), which only
  // ever says WHICH KIND of concept this is ('Ad concept' / 'Post concept' /
  // 'Creative concept'), never which one. Same fallback text as
  // `BriefConceptPage`'s own header leaf for an unnamed concept.
  const conceptLeaf = concept?.hook?.trim() || 'Untitled concept';
  const statusLabel = CONCEPT_DISPLAY_LABEL[workbench.displayStatus];
  const readyGate = workbench.readyGate;
  const isTypographic = isFlatDesignDirection(concept?.visualDirection);
  const rendererNote = useMemo(() => {
    if (!isTypographic) return undefined;
    const older = sizesFromOlderRenderer(
      (workbench.document?.variants ?? []).map((variant) => ({
        variantId: variant.id,
        aspectRatio: variant.aspectRatio,
        hasImage: Boolean(variant.mediaId),
        marker: variant.mediaId ? workbench.mediaCardRenderers[variant.mediaId] : null,
      })),
    );
    return older.length > 0
      ? { olderRendererVariantIds: new Set(older.map((size) => size.variantId)) }
      : undefined;
  }, [isTypographic, workbench.document, workbench.mediaCardRenderers]);

  const actionsMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <CircleIconButton size="header" ariaLabel="Concept actions" tooltip="Actions">
          <DotsThreeIcon weight="bold" />
        </CircleIconButton>
      </DropdownMenuTrigger>
      {/* `container` is load-bearing here, not decoration. Workspace inspectors
          render raw `DialogPrimitive.Content`; a menu portalled to
          `document.body` is a DOM SIBLING of that content, so dismissing the
          menu reads as a click outside the dialog and cascades the whole
          workbench closed. Portalling inside the content makes it a real
          descendant, which Radix can never misread. */}
      <DropdownMenuContent align="end" className="w-64" container={container}>
        <DropdownMenuItem disabled={!isDirty || isSaving} onSelect={() => void saveRef.current()}>
          <FloppyDiskIcon />
          <span>{isSaving ? 'Saving…' : 'Save concept'}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!hasDownload}
          className="md:hidden"
          onSelect={() => void downloadRef.current()}
        >
          <DownloadSimpleIcon />
          <span>Download this image</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!readyGate.ok}
          onSelect={() => toast.message('Ready is set on the brief.')}
        >
          <CheckCircleIcon />
          <span>{readyGate.ok ? 'Mark Ready' : readyGate.tooltip}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate(`/briefs/${briefId}/concepts/${projectTaskId}`)}>
          <ArrowSquareOutIcon />
          <span>Open in brief</span>
        </DropdownMenuItem>
        {/* ARCHIVE / RESTORE. Both RPCs and both mutations already existed and
            this route offered neither, so an archived — and therefore read-only
            — concept could still be opened here and designed on. The row is
            never a guess about which direction applies: it reads the concept's
            own status. */}
        <DropdownMenuItem disabled={lifecycle.isPending} onSelect={() => void lifecycle.run()}>
          {concept?.status === 'archived' ? <ArrowCounterClockwiseIcon /> : <ArchiveIcon />}
          <span>{concept?.status === 'archived' ? 'Restore concept' : 'Archive concept'}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const header = (
    /* The tooltips on these icon-only controls need a provider in scope. The app
       mounts one at the root, but this body also renders inside Sheets and the
       style guide, so it carries its own rather than depending on an ancestor it
       cannot see. */
    <TooltipProvider delayDuration={200}>
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border/40 px-3">
        {/* TOOL CHROME, not page chrome — see `docs/design-system/TOOLBAR-DOCTRINE.md`.
            The ad & brief studio is a toolbar INSIDE a surface, so its icons run
            bare on desktop and cluster only on touch. `.header-action-pill` is
            exactly that treatment: a capsule below 768px, transparent and
            borderless from `md:` up. Both ends of this row use it, so the back
            arrow and the trailing cluster stay the same object type at both
            breakpoints — which is what `.header-nav-btn` (a capsule at every
            width) could not give.
            It renders at every width here, unlike `PageHeader`'s `md:hidden` nav
            slot, because this body covers `PageHeaderManager` entirely — there is
            no breadcrumb root crumb on desktop to carry "back" instead. */}
        <div className="header-action-pill inline-flex items-center">
          <CircleIconButton
            size="header"
            ariaLabel="Back to brief"
            tooltip="Back to brief"
            /* SIDE FIX — this header sits flush against the top of the
               viewport (no `PageHeader` above it, see the file header
               comment), so `top` — the default side — has zero headroom and
               Radix's collision avoidance flips it to `bottom`, landing the
               tooltip directly on the Layers rail's own "Layers" label at
               first paint (`P0-design-mode-4x5.png`). `right` keeps it inside
               the header row and clear of the rail below on every REAL
               hover/focus this button gets later. (The auto-focus-on-mount
               flash this tooltip used to show — Radix Dialog autofocuses the
               first tabbable descendant, and Radix's TooltipTrigger opens on
               ANY focus event — is now fixed generically at the shared
               `src/components/ui/tooltip.tsx`, not with a local blur; see
               that file's `Tooltip`/`TooltipTrigger` doc comments.) */
            tooltipSide="right"
            onClick={onClose}
          >
            <ArrowLeftIcon />
          </CircleIconButton>
        </div>
        {/* THE HIERARCHY BREADCRUMB — founder, 2026-08-15: "the url says its
            nested under brief and concepts but the breadcrumb nesting there
            doesn't reflect that at all... I have no idea how to go to brief
            or even go to that concept." Mirrors the URL
            (`/briefs/:briefId/concepts/:projectTaskId/design`) with a real,
            navigable crumb per segment — `Briefs` → `/briefs`, the brief name
            → `/briefs/:briefId`, the concept's HOOK → `/briefs/:briefId/
            concepts/:projectTaskId`, `Design` is the current page.

            This body covers `PageHeaderManager`/`RouteBreadcrumb` entirely
            (workspace-presentation `Inspector` renders a raw
            `DialogPrimitive.Content`, see the file header comment), so
            `RouteBreadcrumb` itself cannot be reused here. The shared
            `Breadcrumb` primitive is used by `ConceptDesignBreadcrumb`, which
            gives this four-level hierarchy bounded, measured fade labels while
            `InspectorBreadcrumb` remains intentionally limited to a flat
            first-and-last pair.

            The hook is deliberately the identity segment, not `displayLabel`
            ("Ad concept"/"Post concept"/"Creative concept") — that string only
            ever says WHICH KIND of concept this is, never which one
            (`getConceptDisplayLabel`, `concept-contract.ts:113-121`).

            `Design` as the trailing crumb absorbs the job the old "Design
            canvas" caption did (the second NN/g mode signal distinguishing
            this shell from the organic post editor's) — it is NOT
            canvasView-dependent (Design/Preview is a client-only toggle;
            the URL segment is a fixed route suffix), so the signal still
            survives Preview mode exactly as the old caption did. Keeping
            BOTH a "Design canvas" caption AND a "Design" crumb would have
            said the same thing twice in an already-dense bar — subtraction,
            not deletion. `displayLabel` (the ad/post/creative type) is
            dropped from the header for the same reason: it remains visible
            inside the concept brief sheet, and stacking [breadcrumb ×4][type
            caption][status badge] in a 48px bar crowded the one thing this
            fix is actually about. Flagged in the handoff report as a call
            beyond the literal ask, easy to restore as a caption if wanted. */}
        <ConceptDesignBreadcrumb
          briefId={briefId}
          projectTaskId={projectTaskId}
          briefName={briefName}
          conceptLeaf={conceptLeaf}
        />
        <div className="flex shrink-0 items-center gap-2">
          <StatusBadge
            kind="generic"
            tone={
              workbench.displayStatus === 'ready' || workbench.displayStatus === 'live'
                ? 'success'
                : workbench.displayStatus === 'in-design'
                  ? 'info'
                  : 'neutral'
            }
          >
            {statusLabel}
          </StatusBadge>
          {/*
            THE READY GATE, VISIBLE AT LAST — the same `QualityChip` the organic
            post editor uses, so a marketer moving between the two surfaces
            reads one affordance rather than learning two.

            It lives beside the status badge because both answer "what state is
            this in": the badge says where the concept is, the chip says what is
            standing between it and the next state. It is deliberately NOT in
            the right rail — that rail scrolls, and a gate you have to go
            looking for is the thing this replaces.

            What it replaces: a DISABLED row in `⋯ → Actions` whose label was
            the blocker tooltip, sitting directly beneath an enabled `Save
            concept` row. The two read as one sentence at a glance. That row
            stays (it is where you MARK Ready, once the gate passes); it is no
            longer the only place the gate is legible.
          */}
          <QualityChip
            verdict={readyGateChipVerdict(workbench.document, rendererNote)}
            copy={READY_GATE_CHIP_COPY}
            className="hidden sm:inline-flex"
          />
          {/*
            🔴 THIS SCREEN HAS TWO INDEPENDENT SAVE MODELS, AND ONLY ONE OF
            THEM WAS VISIBLE. Traced 2026-08-21 at the founder's request —
            "when I save this page here, the change is actually saved, and
            where does that sit at?":

            1. LAYER WORK (drag a text layer, recolour, reframe) autosaves.
               `AutosaveQueue` (`lib/concept-workbench/autosave.ts`) debounces
               800ms per rendition, coalesces patches, never runs two writes in
               flight for one key, and CASes on `version`. It writes
               `concept_renditions` / `concept_rendition_sets` via
               `ConceptRenditionService`. This is what `AutoSaveIndicator`
               reports.

            2. CONCEPT FIELDS (Persona, Angle, Headline, Caption, CTA — the
               whole right rail) do NOT autosave. Spec §6.8 makes that
               deliberate: the brief is "an explicit, versioned, human-reviewed
               Save". It writes `project_tasks` through
               `conceptEditor.update` with an `expectedVersion`.

            The gap: "Saved" has never referred to model 2, and model 2's only
            affordance was a `⋯ → Save concept` row you had to OPEN THE MENU to
            discover — its `disabled={!isDirty}` state was the sole indication
            that unsaved work existed. Edit a headline, glance at the header,
            read "Saved", and you would reasonably believe it was.

            This pill is that missing indicator, and it is not a permanent
            addition to a row the founder already finds crowded: it renders
            ONLY while there is unsaved concept work, and it IS the save
            action, so it also retires the reason to open the menu.
          */}
          {isDirty ? (
            <button
              type="button"
              disabled={isSaving}
              onClick={() => void saveRef.current()}
              aria-label="Save concept — you have unsaved changes"
              className={cn(
                statusBadgeVariants({ tone: 'warning' }),
                'transition-opacity hover:opacity-80 disabled:opacity-60',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
            >
              {isSaving ? 'Saving…' : 'Unsaved — save'}
            </button>
          ) : null}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <AutoSaveIndicator
            isSaving={layerSaving}
            lastSaved={lastLayerSave}
            className="hidden lg:flex"
          />
          {/* THE ACTION CLUSTER GOES THROUGH `PageHeaderActionLayout`, not a
            hand-rolled class. `surface="workspace"` is the intent declaration:
            this is TOOL chrome (see `docs/design-system/TOOLBAR-DOCTRINE.md`), so
            the component runs the icons bare on desktop and clusters them on
            touch. Callers never name the class.
            Using the layout component also buys its responsive grammar for free:
            if a labelled primary is ever added back here, desktop keeps it beside
            the group while mobile fuses it in — the rule that keeps a lone
            primary from acquiring an empty capsule. Undo/redo/download are
            DESKTOP-only, so they are passed as `desktopActions` rather than
            hidden with a `md:` class inside one shared group: hiding them left a
            mobile capsule padded for five children and drawn around two.
            Download stays reachable on mobile as a menu row, so the capability
            never disappears. */}
          <PageHeaderActionLayout
            surface="workspace"
            desktopActions={
              <>
                <CircleIconButton
                  size="header"
                  ariaLabel="Concept brief"
                  tooltip="Concept brief"
                  onClick={() => setBriefSheetOpen(true)}
                >
                  <NewspaperClippingIcon />
                </CircleIconButton>
                <CircleIconButton
                  size="header"
                  ariaLabel="Undo"
                  tooltip="Undo"
                  disabled={!workbench.canUndo}
                  onClick={undo}
                >
                  <ArrowUUpLeftIcon />
                </CircleIconButton>
                <CircleIconButton
                  size="header"
                  ariaLabel="Redo"
                  tooltip="Redo"
                  disabled={!workbench.canRedo}
                  onClick={redo}
                >
                  <ArrowUUpRightIcon />
                </CircleIconButton>
                <CircleIconButton
                  size="header"
                  ariaLabel="Download this image"
                  tooltip="Download this image"
                  disabled={!hasDownload}
                  onClick={() => void downloadRef.current()}
                >
                  <DownloadSimpleIcon />
                </CircleIconButton>
                {actionsMenu}
              </>
            }
            mobileActions={
              <>
                <CircleIconButton
                  size="header"
                  ariaLabel="Concept brief"
                  tooltip="Concept brief"
                  onClick={() => setBriefSheetOpen(true)}
                >
                  <NewspaperClippingIcon />
                </CircleIconButton>
                {actionsMenu}
              </>
            }
          />
        </div>
      </header>
    </TooltipProvider>
  );

  const handleSelectLayer = useCallback(
    (id: string) => selectLayers(id === '__background__' ? [] : [id]),
    [selectLayers],
  );

  /**
   * Generating from NOTHING is how invented products get shipped, so the
   * button is refused rather than sent when the concept genuinely has no
   * material to work from — but "genuinely nothing" is narrower than "the
   * subject field is blank".
   *
   * 🔴 THE FIX (LANE IMG-DONE). Three straight rounds of the founder's own
   * quality loop hit the SAME concept shape: `visual_direction.subject` and
   * `.setting` both empty (`intent: 'none'`), Generate permanently disabled,
   * "Add a subject in Brief details before generating." — on a concept that
   * still carried a real hook, a sourced proof quote, and an on-image
   * headline. The brief pipeline had already written everything an ad
   * artwork needs to be ABOUT; one blank field was treated as "nothing to
   * generate from" instead of "no explicit visual direction was authored,"
   * and a brief-created concept could not reach one click without a human
   * first opening Brief details and typing a subject by hand.
   *
   * `deriveDesignPrompt` falls through subject → proof quote → on-image
   * headline/hook → rationale — the same "most concrete fact first" order
   * the ad-copy grounding already uses elsewhere in this pipeline. Only when
   * NONE of these exist does the button stay refused, and the reason names
   * every field that would unblock it, not just "a subject".
   */
  const designPromptGate = useMemo(() => resolveDesignPromptGate(concept), [concept]);

  /**
   * A size already edited by prompt is the current design; the Concept's stored visual direction is
   * the plan the FIRST image came from. "Generate this size" carries the sibling forward (outpaint
   * from its image, with the Concept's direction dropped from the prompt) and only falls back to the
   * stored direction when no size has an image yet.
   */
  const siblingReference = useMemo(
    () =>
      resolveSiblingDesignReference({
        concept,
        selectedVariantId: selectedVariant?.id ?? null,
        variants: document?.variants ?? [],
        mediaUrls: workbench.mediaUrls,
      }),
    [concept, selectedVariant?.id, document, workbench.mediaUrls],
  );
  const generatePlan = useMemo(
    () => planGenerateThisSize({ gate: designPromptGate, sibling: siblingReference }),
    [designPromptGate, siblingReference],
  );
  const canGenerateFromBrief = generatePlan.kind !== 'none';

  const generateFromBrief = useCallback(async () => {
    if (generatePlan.kind === 'sibling') {
      const mode = PROMPT_MODES.find((option) => option.editMode === 'outpaint');
      if (mode) {
        await prompt.submit(
          mode,
          siblingDesignInstruction(generatePlan.reference, selectedVariant?.aspectRatio),
          {
            imageUrl: generatePlan.reference.imageUrl,
            mode,
            recompose: generatePlan.reference.flat,
          },
        );
        return;
      }
    }
    if (generatePlan.kind === 'card-spec' || generatePlan.kind === 'direction') {
      await prompt.submit(undefined, generatePlan.prompt);
    }
  }, [generatePlan, prompt, selectedVariant?.aspectRatio]);

  /**
   * W-2. `Brand lockup` is appended only once a real logo has resolved (see
   * `brandLogoUrl` above); with none it still renders so the row is
   * discoverable, but disabled with the honest reason rather than silently
   * doing nothing if somehow clicked.
   */
  const addLayerItems = useMemo(
    () => [
      ...BASE_ADD_LAYER_ITEMS,
      {
        id: 'logo',
        label: 'Brand lockup',
        description: brandLogoUrl
          ? 'Your brand mark, sized and positioned automatically'
          : 'Add a logo to your brand kit first',
        disabled: !brandLogoUrl,
      },
    ],
    [brandLogoUrl],
  );

  /**
   * A new layer is a SET-level act: it exists in every size, or it is a bug.
   * `addLayer` seeds per-ratio geometry for each variant from that ratio's own
   * stage, so a headline added while looking at 4:5 lands sensibly in 9:16
   * instead of inheriting a portrait's coordinates.
   */
  const handleAddLayer = useCallback(
    (itemId: string) => {
      const id = `${itemId}-${crypto.randomUUID()}`;
      if (itemId === 'text') {
        addLayer({
          id,
          kind: 'text',
          name: 'Text',
          zIndex: 0,
          visible: true,
          text: draft?.hook?.trim() || 'Your headline',
          fontScale: 0.06,
          fontFamily: brandFontFamily,
          fontWeight: 700,
          isItalic: false,
          isUnderline: false,
          textTransform: 'none',
          textAlign: 'left',
          verticalAlign: 'top',
          lineHeight: 1.2,
          letterSpacing: 0,
          fill: '#ffffff',
          contrastMode: 'drop-shadow',
          contrastColor: '#000000',
          contrastIntensity: 50,
          contrastPadding: { top: 0, right: 0, bottom: 0, left: 0 },
          boxRoundness: 0,
        });
      } else if (itemId === 'shape') {
        addLayer({
          id,
          kind: 'shape',
          name: 'Shape',
          zIndex: 0,
          visible: true,
          shapeType: 'rectangle',
          fill: '#000000',
          // 🔴 40, NOT 0.4. `KonvaShapeNode` renders `element.opacity / 100`, so
          // the old 0.4 seed produced a scrim at 0.4% — a layer that appeared in
          // the panel, took selection, moved on drag and was invisible on the
          // artwork. Found by reading the renderer while wiring the shape
          // inspector, where the new Opacity row would have read "0".
          opacity: 40,
          feather: 24,
        });
      } else if (itemId === 'logo') {
        // Refused rather than seeded with an empty string: the honesty gate
        // lives in `addLayerItems` above (the row renders disabled with the
        // reason stated), but a stale click on a since-disabled row — or a
        // brand kit that lost its logo between renders — must not create a
        // layer with nothing to render.
        if (!brandLogoUrl) return;
        addLayer({
          id,
          kind: 'logo',
          name: 'Brand lockup',
          zIndex: 0,
          visible: true,
          logoUrl: brandLogoUrl,
          logoVariant: 'primary',
          opacity: 1,
        });
      } else {
        return;
      }
      selectLayers([id]);
    },
    [addLayer, draft, brandLogoUrl, brandFontFamily, selectLayers],
  );

  const handleAddRatio = useCallback(
    (ratio: AspectRatioToken) => {
      void persistence.addRatio(ratio).catch((cause: unknown) => {
        logger.error('Workbench add ratio failed', cause);
        toast.error(cause instanceof Error ? cause.message : 'Could not add that size.');
      });
    },
    [persistence],
  );

  /**
   * REORDER — the fix for a live silent no-op. `LayersPanel` has always drawn a
   * drag handle and bound ⌥↑/⌥↓, and this page never passed `onReorder`, so the
   * panel's own guard returned early, `draggable` stayed false and the whole
   * affordance was dark.
   *
   * The panel lists TOP of the z-stack first and the store keys painting on
   * ascending `zIndex`, so the two orders are inverted; the background is a
   * synthetic locked row that is not a store layer at all and is dropped before
   * the stack is rewritten. `setLayerOrder` takes the whole stack so one drag is
   * one history entry rather than N adjacent swaps.
   */
  const handleReorder = useCallback(
    (from: number, to: number) => {
      const next = nextLayerOrder(layers, from, to);
      if (next) setLayerOrder(next);
    },
    [layers, setLayerOrder],
  );

  const briefFields = draft ? (
    <>
      <WorkbenchBriefFields
        draft={draft}
        referenceUrls={draft.referenceUrls}
        onChange={(patch) => setDraft((current) => (current ? { ...current, ...patch } : current))}
      />
      {/* PROVENANCE, read-only, at the bottom of the brief. This is the surface
          where a marketer is about to spend a credit on a generation, so "what
          is this visual direction based on?" has to be answerable here rather
          than one page over. It is deliberately the LAST thing in the sheet and
          collapsed by default: it is context for a decision, not a field. */}
      <div className="border-t border-border/40 px-3 py-3">
        <StoredContextDisclosure
          title="Brief context"
          value={{
            grounding: concept?.grounding ?? null,
            planningAnatomy: concept?.anatomy ?? null,
          }}
          emptyLabel="No Brief context is stored."
        />
      </div>
    </>
  ) : (
    <div className="flex flex-col gap-2 p-3">
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
    </div>
  );

  /**
   * THE LEFT RAIL IS LAYERS AND NOTHING ELSE.
   *
   * The concept brief used to live stacked underneath, and it is the reason this
   * column read cramped: six prose fields under a layer list is a form wearing a
   * rail. Widening alone would not have fixed it — a wider form is still a form.
   * The brief moved behind the header's brief affordance into a centred DIALOG
   * (a Sheet until 2026-08-15 — see the ruling at that dialog's own comment
   * below), where it gets a readable measure and its own explicit,
   * version-checked Save beside the fields that save writes. It stays ONE click
   * from the canvas, which is what the stacked layout was actually protecting
   * (the brief is read constantly while editing), and the rail goes back to
   * being structure.
   */
  const leftColumn = (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <LayersPanel
        layers={layers}
        selectedId={workbench.selectedLayerIds[0] ?? null}
        onSelect={handleSelectLayer}
        onToggleVisibility={(id, hidden) => setLayerVisibility(id, !hidden)}
        onReorder={handleReorder}
        /**
         * `deleteLayer` shipped in the store with the workbench and had no
         * reachable UI for weeks — layers could be added and never removed.
         * Same class as the unreachable logo property group and the design
         * route before it got a link: built, then wired to nothing. It goes
         * through the store's own action, so it is one undo entry and it
         * cleans up the per-variant geometry and overrides with it.
         */
        onDelete={deleteLayer}
        addItems={addLayerItems}
        onAdd={handleAddLayer}
        className="min-h-0 flex-1 overflow-y-auto"
      />
      {/*
       * THE RAIL'S FOOTER — the answer to "the left column reads empty".
       *
       * Measured at 1440×900: `Layers` held ONE row and then 617px of nothing,
       * in a 216px column. Two candidate fixes were on the table and narrowing
       * the rail is the wrong one: 216px already tracks the reference (210px)
       * and the founder has already pushed this column DOWN once from 260–320
       * ("the left panel is too wide"), so narrowing again buys ~30px of canvas
       * and costs the layer names their measure.
       *
       * The void is not a width problem, it is a "this column never told you
       * what it can do" problem. Drag-to-reorder and ⌥↑/⌥↓ only shipped in part
       * 1 and are invisible until you happen to hover a row; the `+` is a 20px
       * glyph. So the footer states the two things the list can do, pinned to
       * the bottom of the rail where it cannot be mistaken for a list row, and
       * it disappears the moment the list is long enough to fill the column
       * itself.
       */}
      {layers.length === 0 ? (
        <p className="shrink-0 px-4 pb-4 text-detail text-muted-foreground">
          Add a layer, or generate an image to design over.
        </p>
      ) : (
        <div className="mt-auto shrink-0 border-t border-border/40 px-4 py-3">
          <p className="text-detail leading-relaxed text-muted-foreground">
            {layers.length > 1
              ? 'Drag a row to restack it, or press ⌥↑ / ⌥↓. Delete removes the selected layer.'
              : 'Add text or a shape with +. Layers stack the same way in every size.'}
          </p>
        </div>
      )}
    </div>
  );

  const critiqueOverlayText = useMemo(() => {
    const lines: string[] = [];
    for (const layer of document?.layers ?? []) {
      if (layer.kind !== 'text' || !layer.visible) continue;
      const override = selectedVariant?.overrides[layer.id];
      const text = (override?.text ?? layer.text)?.trim();
      if (text) lines.push(text);
    }
    return lines;
  }, [document, selectedVariant]);

  /**
   * THE EDIT MODES ARE VISIBLE NOW, AND THEY WORK.
   *
   * They used to live behind a `+` dropdown whose rows called
   * `prompt.submit(mode)` directly — and `submit` returns immediately when the
   * prompt is empty, so choosing `Enhance` with nothing typed dispatched
   * nothing at all: no request, no error, no status. A hidden control that also
   * silently failed.
   *
   * A chip now SELECTS a mode and prefills the composer with that mode's
   * instruction, caret at the end, exactly as MediaStudio's suggestion chips
   * do. The user edits the sentence and sends it like any other prompt; the
   * mode rides along on the request. Nothing classifies free text — the
   * three-endpoint split in `useWorkbenchPrompt` is untouched.
   */
  const [modeId, setModeId] = useState<string | null>(null);
  const composerRef = useRef<InlinePromptComposerHandle | null>(null);
  const hasImage = Boolean(workbench.selectedImageUrl);
  const selectedMode = PROMPT_MODES.find((mode) => mode.id === modeId) ?? undefined;

  const regenerateSize = useCallback(async () => {
    if (!designPromptGate.ok) return;
    await prompt.submit(undefined, designPromptGate.prompt, undefined, { fresh: true });
  }, [designPromptGate, prompt]);

  const [regenAllIndex, setRegenAllIndex] = useState<number | null>(null);
  const regenerableSizes = useMemo(
    () =>
      isTypographic
        ? (document?.variants ?? [])
            .filter((variant) => Boolean(variant.mediaId))
            .sort((a, b) => a.position - b.position)
        : [],
    [isTypographic, document],
  );
  const regenerateAllSizes = useCallback(async () => {
    if (!designPromptGate.ok) return;
    const sizes = regenerableSizes.map((variant) => ({
      id: variant.id,
      aspectRatio: variant.aspectRatio,
    }));
    try {
      for (let index = 0; index < sizes.length; index += 1) {
        setRegenAllIndex(index);
        await prompt.submit(undefined, designPromptGate.prompt, undefined, {
          fresh: true,
          variant: sizes[index],
        });
        if (!prompt.lastImageAttached()) break;
      }
    } finally {
      setRegenAllIndex(null);
    }
  }, [designPromptGate, prompt, regenerableSizes]);

  const modeRail = (
    <>
      <WorkbenchPromptModeRail
        modes={PROMPT_MODES.map((mode) => ({
          id: mode.id,
          label: mode.label,
          description: mode.description,
          disabled: !isModeAvailable(mode, hasImage),
        }))}
        selectedId={modeId}
        onSelect={(id) => {
          setModeId(id);
          const picked = PROMPT_MODES.find((mode) => mode.id === id);
          if (!picked) return;
          prompt.setValue(picked.defaultPrompt);
          requestAnimationFrame(() => composerRef.current?.focusAndCaretEnd());
        }}
      />
    </>
  );

  const sizeActions = (
    <>
      {selectedVariant ? (
        <RegenerateSizeChip
          aspectRatio={selectedVariant.aspectRatio}
          disabled={
            !hasImage || !designPromptGate.ok || prompt.pending || selectedVariant.isGenerating
          }
          disabledReason={
            designPromptGate.ok
              ? 'Nothing to regenerate yet'
              : (designPromptGate.reason ?? undefined)
          }
          onConfirm={() => void regenerateSize()}
        />
      ) : null}
      {regenerableSizes.length > 1 ? (
        <RegenerateAllSizesChip
          aspectRatios={regenerableSizes.map((variant) => variant.aspectRatio)}
          runningIndex={regenAllIndex}
          disabled={
            !designPromptGate.ok ||
            prompt.pending ||
            regenerableSizes.some((variant) => variant.isGenerating)
          }
          disabledReason={
            designPromptGate.ok ? 'A size is generating' : (designPromptGate.reason ?? undefined)
          }
          onConfirm={() => void regenerateAllSizes()}
        />
      ) : null}
    </>
  );

  const inspector = (
    <WorkbenchInspector
      variants={variants}
      selectedRatio={selectedVariant?.aspectRatio ?? '1:1'}
      onSelectRatio={(ratio) => {
        const match = document?.variants.find((variant) => variant.aspectRatio === ratio);
        if (match) selectVariant(match.id);
      }}
      onAddRatio={handleAddRatio}
      sizeActions={sizeActions}
      platforms={matrixPlatforms}
      conceptId={projectTaskId}
      briefId={briefId}
      selectedCopyVariantId={selectedCopyVariantId}
      onSelectCopyVariantId={setSelectedCopyVariantId}
      brandLogoUrls={brandLogoUrls}
      critique={
        selectedVariant
          ? {
              conceptId: projectTaskId,
              renditionId: selectedVariant.id,
              adUnitId: selectedCopyVariantId,
              mediaId: selectedVariant.mediaId ?? document?.mediaId ?? null,
              overlayText: critiqueOverlayText,
              actions: {
                onWriteCopy: () =>
                  globalThis.document
                    ?.getElementById('concept-content-section')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
                onEditImage: (suggestion) => {
                  setCanvasView('design');
                  prompt.setValue(suggestion);
                  requestAnimationFrame(() => composerRef.current?.focusAndCaretEnd());
                },
                onGenerateSize: canGenerateFromBrief ? () => void generateFromBrief() : undefined,
              },
            }
          : undefined
      }
      conceptSection={
        draft ? (
          <ConceptFieldsSection
            briefId={briefId}
            draft={draft}
            onChange={(patch) =>
              setDraft((current) => (current ? { ...current, ...patch } : current))
            }
          />
        ) : null
      }
      layer={selectedLayer}
      geometry={
        selectedLayer && selectedVariant
          ? (selectedVariant.geometry[selectedLayer.id] ?? null)
          : null
      }
      variant={selectedVariant}
      onLayerChange={(patch) => {
        if (selectedLayer) updateSetLayer(selectedLayer.id, patch);
      }}
      onGeometryChange={(patch) => {
        if (selectedLayer && selectedVariant) {
          updateGeometry(selectedVariant.id, selectedLayer.id, patch);
        }
      }}
      className="min-h-0"
    />
  );

  const centre = (
    /* `flex-1` is load-bearing on MOBILE, not on desktop. In the grid the track
       stretches it for free; in the mobile flex column it does not, and without
       it the canvas collapsed to ~60px with the empty state and the prompt bar
       stacked on top of each other. Verified at 390px. */
    /* THE ONE TONAL STEP — `--workbench-well`, NOT `--canvas`. This well must
       always sit DARKER than `--background`, in BOTH themes — that is the
       whole rule, and it used to be wrong on this surface. `--canvas` is
       shared with MediaStudio and the post-preview pane and is tuned to each
       surface's own job; reusing it here silently inherited dark theme's
       `240 6% 9%`, which is LIGHTER than dark `--background` (6.67%), so the
       artboard read as raised above the tool chrome instead of recessed
       beneath it — measured live, well-vs-rail was ~1.052:1 with the well on
       the WRONG side of the page. `--workbench-well` fixes only the
       direction: light stays `36 18% 97%` (already correct, one step under
       `--background` 99%), dark moves to `240 6% 4.5%` (one step under
       `--background` 6.6667%, not over it).
       The MAGNITUDE is deliberately small and is not a bug to fix later — it
       matches this app's own `--card`-vs-`--background` step and Hightouch's
       measured well-vs-rail step (~1.03–1.08:1), both real shipped products.
       Making the step bigger reproduces a failure already paid for once: an
       earlier pass painted the side rails with their own fill against a dark
       well and produced "sidebars everywhere" (founder). The rails still
       carry NO fill of their own — zero-fill on the page, separated from the
       well only by the `border-border/40` hairline — that treatment is
       settled and stays exactly as it is. */
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-workbench-well">
      {selectedVariant ? (
        <>
          {/* THE DESIGN ⇄ PREVIEW SWITCH — a genuine one-of-N state, so
              `SegmentedToggle` rather than two independent buttons. On
              DESKTOP it floats top-right of the canvas, clear of the
              ratio/zoom readout (`WorkbenchStage`, top-left) and the floating
              prompt bar (bottom-centre, Design mode only). It is NOT rendered
              here on mobile — at 390px the stage's own top-left readout
              ("4:5 · 100% · view only on this screen size") already reaches
              most of the width, and a second floating control on the right
              collided with it. Mobile gets the toggle in the existing top
              bar instead (below), which already has room budgeted for
              exactly two icon-sized controls either side of the ratio label. */}
          {canSplit ? (
            /* ONE TOOL RAIL, not two floating controls. The platform switcher
               used to sit centred over the canvas above the preview card,
               which read as a caption belonging to the card rather than as
               chrome belonging to the surface. Founder, 2026-08-21: "put the
               platform icons stacked vertically and below design/preview
               aligned against that border line… more like a toolbar."

               Stacking them here rather than inside `ConceptPreviewPane` is
               what lets them reach the edge at all: that pane is width-capped
               and centred, so its right edge is nowhere near the rail. Only a
               sibling of the scroll container can align to it — which also
               means the rail stays put when a tall preview scrolls, exactly
               like the Design/Preview toggle above it. */
            <div className="absolute right-3 top-3 z-30 flex flex-col items-end gap-2">
              <SegmentedToggle<'design' | 'preview'>
                value={canvasView}
                onValueChange={setCanvasView}
                options={[
                  { value: 'design', label: 'Design' },
                  { value: 'preview', label: 'Preview' },
                ]}
                variant="pills"
                size="sm"
                /* One of the two cases the capsule still earns: this toggle is
                   `absolute` over the artwork, so its ground is whatever the
                   render happens to be — a white product shot swallows a
                   groundless chip whole. See `SegmentedToggle`'s `ground` note;
                   everything on a normal page ground is `'none'`. */
                ground="group"
              />
              {canvasView === 'preview' ? (
                /* The `role="group"` wrapper carries the accessible name: this
                   toggle runs `semantics="toggle"`, and `ariaLabel` only lands
                   on the container when it renders a tablist. Dropping the
                   wrapper when the control moved here would have left a column
                   of three unlabelled icon buttons. */
                <div role="group" aria-label="Ad preview platform">
                  <SegmentedToggle<AdPlatformId>
                    value={selectedAdPlatform ?? resolvedAdPlatform ?? 'meta'}
                    onValueChange={setSelectedAdPlatform}
                    options={AD_PLATFORM_OPTIONS}
                    variant="default"
                    orientation="vertical"
                    ariaLabel="Ad preview platform"
                    size="sm"
                    ground="group"
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          {canvasView === 'design' ? (
            <>
              {/* `pb-32` RESERVES the floating bar's lane, and the number is measured
                  rather than inherited. Without a reserve the artwork centres into
                  the full well and the bar sits ON it — part 1 measured the artwork
                  running to y=845 under a bar at 815–876, hiding the second line of
                  the headline.
                  `pb-16` (64px) was correct for a ~61px bar. The bar now carries the
                  edit-mode chip rail on the composer's own quick-actions row and
                  measures 102px, so at `bottom-6` it occupies 773–875 and the reserve
                  has to be 24 + 102 = 126px. Re-measured with `pb-16` still in place:
                  the artwork ran to 781.1 against a bar top of 773 — an 8.1px
                  overlap, i.e. the regression had already happened and was invisible
                  at this concept's aspect ratio only by luck. `pb-32` is 128px. */}
              <div className="relative flex min-h-0 flex-1 flex-col pb-32">
                <WorkbenchStage
                  aspectRatio={selectedVariant.aspectRatio}
                  imageUrl={workbench.selectedImageUrl}
                  variantId={selectedVariant.id}
                  readOnly={isMobile}
                  zoom={zoom}
                  onZoomChange={setZoom}
                />
                {/* The most important empty state in the product: every concept
                    starts here. It offers the CHEAP action as the default —
                    generating one size costs one credit and image quota has already
                    been a live blocker, so "generate all sizes" is never the button
                    the surface presses for you. */}
                {!workbench.selectedImageUrl && selectedVariant.isGenerating ? (
                  <div
                    className="pointer-events-none absolute inset-0 flex items-center justify-center"
                    role="status"
                  >
                    <div className="flex max-w-xs flex-col items-center gap-2 text-center">
                      <Spinner className="size-5 text-muted-foreground" />
                      <p className="text-caption font-medium text-foreground/85">
                        Generating this size
                      </p>
                      <p className="text-detail text-muted-foreground">
                        The image saves to this size when it finishes, even if you leave this page.
                      </p>
                    </div>
                  </div>
                ) : null}
                {!workbench.selectedImageUrl &&
                !selectedVariant.isGenerating &&
                !workbench.isResolvingImage ? (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="pointer-events-auto flex max-w-xs flex-col items-center gap-3 text-center">
                      <ImageSquareIcon
                        className="size-10 text-muted-foreground/50"
                        weight="regular"
                      />
                      <div className="flex flex-col gap-1">
                        <p className="text-caption font-medium text-foreground/85">
                          No image for this size yet
                        </p>
                        {workbench.persistence.timedOutRenditionIds.has(selectedVariant.id) ? (
                          <p className="text-detail text-foreground/85">
                            The last attempt did not finish, so no image was saved. Generating again
                            starts a fresh attempt.
                          </p>
                        ) : null}
                        <p className="text-detail text-muted-foreground">
                          {generatePlan.kind === 'card-spec'
                            ? 'Generate this size from the card text and brand, the same way as the first size.'
                            : generatePlan.kind === 'sibling'
                              ? `Generate this size from the current ${generatePlan.reference.aspectRatio} design.`
                              : designPromptGate.ok
                                ? DESIGN_PROMPT_SOURCE_COPY[designPromptGate.source]
                                : designPromptGate.reason}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="neutral"
                        size="sm"
                        disabled={!canGenerateFromBrief || prompt.pending}
                        title={canGenerateFromBrief ? undefined : designPromptGate.reason}
                        onClick={() => void generateFromBrief()}
                      >
                        Generate this size
                      </Button>
                      {/* THE EXISTING-MEDIA SOURCE (audit unit #9). A Studio
                          generation, an Assets upload — anything already in
                          this workspace's `media` table — reaches
                          `concept_renditions` through this button without
                          spending a second image credit re-generating it. */}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={prompt.pending}
                        onClick={() => setExistingMediaOpen(true)}
                      >
                        Use an existing image
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
              {/* The ratio/zoom readout used to live here as centred dead text with a
                  `pb-24` magic number holding it clear of the floating bar. It now
                  lives inside `WorkbenchStage` as a real fit/reset control, pinned
                  bottom-left of the canvas where it cannot collide with the bar. */}
              <FloatingPromptBar
                ref={composerRef}
                value={prompt.value}
                onChange={prompt.setValue}
                onSubmit={async (text) => {
                  await prompt.submit(selectedMode, text);
                  setModeId(null);
                }}
                targetLabel={target.label}
                placeholder={target.placeholder}
                pending={prompt.pending}
                quickActions={target.kind === 'image' ? modeRail : undefined}
                status={
                  prompt.error ? (
                    <span className="inline-flex items-center gap-2 text-destructive">
                      {prompt.error}
                      {prompt.recovery ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-1 text-detail text-foreground"
                          onClick={() => void prompt.retryAttachment()}
                        >
                          Retry attachment
                        </Button>
                      ) : null}
                    </span>
                  ) : (
                    prompt.status
                  )
                }
                className="absolute bottom-6 left-1/2 z-20 w-[calc(100%-24px)] max-w-[640px] -translate-x-1/2 xl:w-[86%]"
              />
            </>
          ) : (
            <div className="preview-stage min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
              <ConceptPreviewPane
                conceptId={projectTaskId}
                intents={concept.intents}
                imageUrl={workbench.selectedImageUrl}
                aspectRatio={selectedVariant.aspectRatio}
                hook={(draft ?? concept).hook}
                summary={(draft ?? concept).summary}
                cta={(draft ?? concept).cta}
                selectedCopyVariantId={selectedCopyVariantId}
                adPlatform={selectedAdPlatform}
                onAdPlatformChange={setSelectedAdPlatform}
                onResolvedAdPlatformChange={setResolvedAdPlatform}
                /* P0 FIX — Preview mode used to pass the raw image straight
                   into the ad shells with no compositing step, so a headline
                   authored in Design mode was silently absent here: the
                   preview lied about what would ship. This reuses the SAME
                   Konva render path Design mode uses (`WorkbenchStage`), in
                   its chromeless `presentation="composite"` mode, rather than
                   inventing a third overlay-layer renderer alongside the
                   client Konva engine and the server opentype.js+sharp
                   compositor. `imageUrl={null}` is deliberate: the real image
                   is drawn by `MediaSlot` underneath (via `overlay`/
                   `mediaOverlay`), and this stage draws only the layer stack
                   on a transparent background. Gated on `selectedImageUrl` so
                   an empty-state size (no image yet) doesn't show authored
                   text floating over nothing. */
                overlay={
                  workbench.selectedImageUrl ? (
                    <WorkbenchStage
                      aspectRatio={selectedVariant.aspectRatio}
                      imageUrl={null}
                      variantId={selectedVariant.id}
                      presentation="composite"
                    />
                  ) : undefined
                }
              />
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-1 items-center justify-center p-6">
          <Empty className="border-0">
            <EmptyHeader>
              <EmptyTitle>No sizes yet</EmptyTitle>
              <EmptyDescription>
                Add the recommended canvas sizes first. Then generate one creative at a time from
                this concept&apos;s visual direction.
              </EmptyDescription>
            </EmptyHeader>
            <Button
              type="button"
              variant="neutral"
              size="sm"
              onClick={() => void persistence.ensureRenditions()}
            >
              Add the recommended sizes
            </Button>
          </Empty>
        </div>
      )}
    </div>
  );

  if (conceptQuery.isLoading) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        {header}
        <div className="flex min-h-0 flex-1 flex-col gap-4 p-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="min-h-0 flex-1" />
        </div>
      </div>
    );
  }

  if (conceptQuery.isError || !concept) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        {header}
        <div className="flex min-h-0 flex-1 items-center justify-center p-6">
          <Empty className="border-0">
            <EmptyHeader>
              <EmptyTitle>This concept doesn&apos;t exist</EmptyTitle>
              <EmptyDescription>
                It may have been archived or removed from the brief.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      </div>
    );
  }

  return (
    <>
      {/*
       * SCROLL CONTAINMENT. Nothing here may ever scroll the shell; each panel
       * scrolls itself. The workspace `Inspector` gives this body an
       * `absolute inset-0 flex min-h-0 flex-col` box, so the explicit
       * `h-full … overflow-hidden` root is what keeps a tall inspector column
       * from stretching that box instead of scrolling inside it. Every flex
       * ancestor down to a scrolling pane carries `min-h-0`, because flex
       * children default to `min-height:auto` and will otherwise push their own
       * box taller than its track.
       */}
      <div className="relative flex h-full min-h-0 flex-col overflow-hidden bg-background">
        {header}
        {canSplit ? (
          <div
            className={cn(
              GRID_COLUMNS_SHELL,
              canvasView === 'preview' ? GRID_TEMPLATE_PREVIEW : GRID_TEMPLATE_DESIGN,
            )}
          >
            {/* `inert` — not just visually collapsed. A 0px-wide column still
                contains real interactive rows (select, drag, ⌥↑/⌥↓), and
                without this a keyboard user could still Tab into a layer row
                they cannot see. The border also drops with the width, or a
                stray 1px rule would sit on the canvas's own left edge with
                nothing to its left justifying it. */}
            <div
              className={cn(
                RAIL_SURFACE,
                'flex flex-col',
                canvasView === 'preview' ? 'border-r-0' : 'border-r border-border/40',
              )}
              inert={canvasView === 'preview'}
            >
              {leftColumn}
            </div>
            {centre}
            <div className={cn(RAIL_SURFACE, 'flex flex-col border-l border-border/40')}>
              {inspector}
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex shrink-0 items-center justify-between border-b border-border/40 px-3 py-2">
              <CircleIconButton
                size="sm"
                hitArea="touch"
                ariaLabel="Open layers and brief"
                onClick={() => setLeftSheetOpen(true)}
              >
                <StackIcon />
              </CircleIconButton>
              {selectedVariant ? (
                <SegmentedToggle<'design' | 'preview'>
                  value={canvasView}
                  onValueChange={setCanvasView}
                  options={[
                    { value: 'design', label: 'Design' },
                    { value: 'preview', label: 'Preview' },
                  ]}
                  variant="pills"
                  ground="group"
                  size="sm"
                />
              ) : (
                <span className="text-detail text-muted-foreground">No size selected</span>
              )}
              <CircleIconButton
                size="sm"
                hitArea="touch"
                ariaLabel="Open inspector"
                onClick={() => setRightSheetOpen(true)}
              >
                <SidebarSimpleIcon />
              </CircleIconButton>
            </div>
            {centre}
          </div>
        )}
      </div>

      <Sheet open={leftSheetOpen} onOpenChange={setLeftSheetOpen}>
        <SheetContent side="left" className="w-full max-w-sm p-0">
          <SheetHeader className="px-3 py-2">
            <SheetTitle>Layers</SheetTitle>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{leftColumn}</div>
        </SheetContent>
      </Sheet>

      <Sheet open={rightSheetOpen} onOpenChange={setRightSheetOpen}>
        <SheetContent side="right" className="w-full max-w-sm p-0">
          <SheetHeader className="px-3 py-2">
            <SheetTitle>Inspector</SheetTitle>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{inspector}</div>
        </SheetContent>
      </Sheet>

      {/*
       * THE CONCEPT BRIEF. A DIALOG rather than a rail section: these are six
       * prose fields, and prose needs a measure. The Save here is the brief's
       * OWN save — explicit, version-checked, and deliberately not merged with
       * the canvas's 800ms autosave, because the brief is a shared reviewed
       * document and the canvas is a private high-frequency artifact.
       *
       * (Was a right-hand Sheet until 2026-08-15. The full ruling — including
       * why the app's inline `FloatingPanel` rail was considered and REJECTED —
       * lives on the Dialog itself further down this file.)
       */}
      {/*
       * A CENTRED DIALOG, NOT A RIGHT-HAND PANEL — founder ruling, 2026-08-15.
       *
       * This was a full-height `Sheet side="right"` that slid over the canvas.
       * Two things were wrong with that, and neither is fixed by restyling it:
       *
       *  1. THIS SCREEN ALREADY HAS THREE PANELS — Layers, canvas, inspector.
       *     A fourth panel, whether it overlays or docks inline, is one more
       *     column competing for the same eye. Converting it to the app's inline
       *     `FloatingPanel` rail (the pattern GlobalRightDock/CalendarDrafts-
       *     Drawer/PlanContextSidebar use) was considered and rejected for
       *     exactly that reason: it is the right primitive on a page that has
       *     room for it, and this page does not.
       *  2. THE BRIEF IS SOMETHING YOU READ. It is prose — intent, subject,
       *     setting, hook, message — scrolled top to bottom, not a property rail
       *     you dip into while dragging a layer. A centred dialog is the shape
       *     the rest of the app already uses for "stop and read/edit this one
       *     object", and `DialogContent` is already scroll-capped
       *     (`max-h-[calc(100vh-4rem)] overflow-y-auto`), so long briefs behave.
       *
       * `max-w-lg` rather than the sheet's old `max-w-md`: prose needs a measure,
       * and the sheet was cramping six textareas into a 448px column.
       */}
      <Dialog open={briefSheetOpen} onOpenChange={setBriefSheetOpen}>
        <DialogContent className="flex max-h-[calc(100vh-4rem)] w-full max-w-lg flex-col gap-0 p-0">
          <DialogHeader className="shrink-0 border-b border-border/40 px-4 py-3">
            <DialogTitle>Concept brief</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto pt-3">{briefFields}</div>
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border/40 px-4 py-3">
            <Button
              type="button"
              variant="neutral"
              size="sm"
              disabled={!isDirty || isSaving}
              onClick={() => void saveRef.current()}
            >
              {isSaving ? (
                <Spinner className="size-4" />
              ) : (
                <FloppyDiskIcon data-icon="inline-start" />
              )}
              {isSaving ? 'Saving…' : 'Save concept'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ExistingMediaSheet
        open={existingMediaOpen}
        onOpenChange={setExistingMediaOpen}
        items={libraryMedia}
        isLoading={isLoadingLibraryMedia}
        error={libraryMediaError}
        onRetry={() => void refetchLibraryMedia()}
        hasMore={libraryMediaHasMore}
        onLoadMore={loadMoreLibraryMedia}
        onSelect={(item) => void handleChooseExistingMedia(item)}
      />
    </>
  );
}
